import { ArrowDropDown } from "@mui/icons-material";
import {
  Box,
  DialogContent,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { memo, useMemo, useState } from "react";
import Baseline from "../../../../components/form/Baseline";
import SearchInput from "../../../../components/inputs/SearchInput";
import formatAmount from "../../../../utils/formatAmount";
import {
  curr,
  CURRENCY_LIST,
  getCurrencySearchFields,
} from "../../../../utils/more/currency_country";
import { CurrencyInputMask } from "../../../../utils/more/mask_functions";
import { includesOnSearch } from "../../../../utils/more/search";

const SelectCurrencyMenu = ({ currency, onChange, disabled }) => {
  const [menu, setMenu] = useState(null);
  const [search, setSearch] = useState("");

  const filteredCurrencies = useMemo(() => {
    return CURRENCY_LIST.filter((item) =>
      includesOnSearch(search, getCurrencySearchFields(item))
    );
  }, [search]);

  const handleCloseMenu = () => {
    setMenu(null);
    setSearch("");
  };

  const handleClickMenuItem = (v) => {
    onChange(v);
    handleCloseMenu();
  };

  return (
    <>
      <Box
        onClick={(e) => !disabled && setMenu(e.currentTarget)}
        borderRadius={1}
        p={0.5}
        pr={disabled ? 1 : 0.5}
        sx={{
          cursor: disabled ? "default" : "pointer",
          ":hover": { bgcolor: "action.hover" },
        }}
        display={"flex"}
        alignItems={"center"}
      >
        <Typography
          flex={1}
          noWrap
          fontSize="1.4rem"
          variant="h5"
          color={disabled ? "text.secondary" : "text.primary"}
          fontWeight={"500"}
        >
          {curr(currency)}
        </Typography>
        {!disabled && <ArrowDropDown fontSize="small" />}
      </Box>
      <Popover
        transitionDuration={0}
        open={Boolean(menu)}
        anchorEl={menu}
        onClose={handleCloseMenu}
        slotProps={{
          paper: {
            sx: {
              width: 330,
              maxHeight: 430,
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <Box p={1.5} pb={1}>
          <SearchInput
            autoFocus
            placeholder="Pesquisar moeda"
            value={search}
            onChange={setSearch}
            sx={{ height: 42 }}
          />
        </Box>
        <DialogContent sx={{ p: 1, pt: 0 }}>
          {filteredCurrencies.map((item) => (
            <ListItemButton
              key={item.code}
              selected={item.code === currency}
              onClick={() => handleClickMenuItem(item.code)}
              sx={{ borderRadius: 1 }}
            >
              <ListItemText
                primaryTypographyProps={{ fontWeight: "600" }}
                primary={item.code}
                secondary={`${item.symbol} - ${item.name}`}
              />
            </ListItemButton>
          ))}
          {!filteredCurrencies.length && (
            <Typography
              color="text.secondary"
              fontSize=".9rem"
              fontWeight="500"
              p={2}
              textAlign="center"
            >
              Nenhuma moeda encontrada
            </Typography>
          )}
        </DialogContent>
      </Popover>
    </>
  );
};

function AmounInput({
  readOnly,
  onChange,
  autoFocus,
  onChangeCurrency,
  value = "",
  innherRef,
  currency,
  sx,
  isEditable = true,
  disabled,
  endAdornment,
  discounted,
  netAmount = 0,
}) {
  if (!isEditable) {
    return (
      <Baseline
        Icon={() => (
          <Typography fontSize={"1.2rem"} fontWeight={"500"}>
            {curr(currency)}
          </Typography>
        )}
        value={
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <Typography
              sx={{ textDecoration: discounted ? "line-through" : "none" }}
              fontSize={"1.4rem"}
              color={discounted ? "textSecondary" : "textPrimary"}
              fontWeight={"600"}
            >
              {formatAmount(value)}
            </Typography>
            {discounted && Boolean(netAmount) && (
              <Typography fontSize={"1.4rem"} fontWeight={"600"}>
                {formatAmount(netAmount)}
              </Typography>
            )}
          </Stack>
        }
      />
    );
  } else {
    return (
      <>
        <Box sx={sx} display={"flex"} gap={1} alignItems="center">
          <SelectCurrencyMenu
            disabled={disabled || readOnly}
            currency={currency}
            onChange={onChangeCurrency}
          />
          <TextField
            name="amount"
            margin="dense"
            hiddenLabel
            required
            disabled={disabled}
            inputRef={innherRef}
            autoFocus={autoFocus}
            fullWidth
            variant={"standard"}
            size="small"
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            placeholder="0,00"
            InputProps={{
              readOnly,
              sx: {
                fontSize: "1.8rem",
                fontWeight: "500",
                borderRadius: 1,
              },
              inputComponent: CurrencyInputMask,
            }}
          />
          {endAdornment && (
            <Box display={"flex"} alignItems={"center"} pl={1}>
              {endAdornment}
            </Box>
          )}
        </Box>
      </>
    );
  }
}

export default memo(AmounInput);
